import React from 'react';
import { LEVELS, RevealLevel, Member, Edge } from '../../lib/a2aApi';
import './a2a.css';
import { useTranslation } from 'react-i18next';

const ORDER: Record<RevealLevel, number> = { redacted: 0, surrogate: 1, full: 2 };

/** Ceiling for an agent = strongest incoming edge ceiling; entry agents (no incoming edge) are unbounded. */
export const ceilingFor = (agent: string, edges: Edge[]): RevealLevel => {
  const inc = edges.filter((e) => e.to === agent);
  if (!inc.length) return 'full';
  return inc.map((e) => e.reveal_max || 'redacted').reduce((a, b) => (ORDER[a] >= ORDER[b] ? a : b)) as RevealLevel;
};

interface Props {
  members: Member[]; edges: Edge[]; classes: string[];
  cells: Record<string, RevealLevel>; justifications?: Record<string, string>;
  onChange?: (agent: string, cls: string, level: RevealLevel) => void;
  onJustify?: (agent: string, cls: string, text: string) => void;
  nameOf?: (id: string) => string;
}

const RevealMatrix: React.FC<Props> = ({ members, edges, classes, cells, justifications = {}, onChange, onJustify, nameOf }) => {
  const { t } = useTranslation();
  return (
  <div style={{ overflowX: 'auto' }}>
    <table className="a2a-matrix">
      <thead><tr><th>{t('components.a2a.revealMatrix.agent')}</th>{classes.map((c) => <th key={c}>{c}</th>)}</tr></thead>
      <tbody>
        {members.map((m) => {
          const ceil = ceilingFor(m.agent_id, edges);
          return (
            <tr key={m.agent_id}>
              <td><strong>{nameOf ? nameOf(m.agent_id) : m.agent_id}</strong> <span className={`a2a-chip ${m.role}`}>{m.role}</span><div className="a2a-muted">{t('components.a2a.revealMatrix.ceiling', { v0: ceil })}</div></td>
              {classes.map((c) => {
                const k = `${m.agent_id}|${c}`; const cur = cells[k] || 'redacted';
                return (
                  <td key={c}>
                    {onChange ? (
                      <div className="a2a-seg">
                        {LEVELS.map((l) => (
                          <button key={l} className={`${cur === l ? `on ${l}` : ''}`} disabled={ORDER[l] > ORDER[ceil]} title={ORDER[l] > ORDER[ceil] ? t('components.a2a.revealMatrix.aboveTheIncomingEdgeCeiling') : l} onClick={() => onChange(m.agent_id, c, l)}>{l === 'redacted' ? t('components.a2a.revealMatrix.redact') : l === 'surrogate' ? t('components.a2a.revealMatrix.surr') : t('components.a2a.revealMatrix.full')}</button>
                        ))}
                      </div>
                    ) : <span className={`a2a-chip ${cur}`}>{cur}</span>}
                    {cur === 'full' && onJustify && <input placeholder={t('components.a2a.revealMatrix.justificationRequired')} value={justifications[k] || ''} onChange={(e) => onJustify(m.agent_id, c, e.target.value)} style={{ marginTop: 4, width: '100%', fontSize: 11, padding: '3px 5px' }} />}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
};

export default RevealMatrix;
